import React, { useState, useEffect } from 'react';
import { animalAlphabetData } from '@/data/animalAlphabetGrid';
import {
  playClickFeedback,
  speakLetterAnimalAndNature,
  prefetchCombinedUtterances,
} from '@/lib/naturalVoiceService';
import type { Language } from '@/lib/voiceManager';

export interface AnimalAlphabetChatEmbedProps {
  lang: Language;
  /** Called with the animal name in the current UI language (fills chat input). */
  onPickAnimal: (animalName: string) => void;
}

/**
 * Compact Animal Alphabet for the Play Together chat: letters + animals + emoji, scrollable.
 * Tap: hear letter + animal + nature/sustainability fun fact; fills the answer field.
 */
const AnimalAlphabetChatEmbed: React.FC<AnimalAlphabetChatEmbedProps> = ({ lang, onPickAnimal }) => {
  const isEn = lang === 'en';
  const [natureNote, setNatureNote] = useState<{ letter: string; animal: string; text: string } | null>(null);

  useEffect(() => {
    const entries = animalAlphabetData.map((item) => ({
      letter: item.letter,
      animal: lang === 'en' ? item.animalEn : item.animalEs,
      natureFact: lang === 'en' ? item.funFactEn : item.funFactEs,
    }));
    void prefetchCombinedUtterances(entries, lang, 6);
  }, [lang]);

  const copy = isEn
    ? {
        title: 'Animal Alphabet',
        intro:
          'Each animal includes a **nature note**—something true about how it lives in the world, so we can connect literacy to **ecological thinking** and **caring for life on Earth**.',
        hint: 'Tap a letter to hear it and the nature note — your answer fills in below (edit if you like).',
        natureHeading: 'Nature & sustainability',
      }
    : {
        title: 'ABC de animales',
        intro:
          'Cada animal incluye una **nota de naturaleza**—algo real sobre cómo vive en el mundo, para conectar la lectura con el **pensamiento ecológico** y el **cuidado de la vida en la Tierra**.',
        hint: 'Toca una letra para escucharla y la nota de naturaleza — tu respuesta se pone abajo (puedes editarla).',
        natureHeading: 'Naturaleza y sostenibilidad',
      };

  return (
    <div
      style={{
        alignSelf: 'stretch',
        marginTop: 8,
        marginBottom: 8,
        padding: '12px 10px',
        borderRadius: 14,
        border: '1px solid rgba(22, 101, 52, 0.2)',
        background: 'linear-gradient(180deg, #F0FDF4 0%, #FFFEF9 100%)',
        maxHeight: 340,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div>
        <div
          style={{
            fontFamily: "'Nunito', sans-serif",
            fontWeight: 800,
            fontSize: '16px',
            color: '#14532D',
            marginBottom: 6,
          }}
        >
          {copy.title}
        </div>
        <div
          style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: '14px',
            color: '#4B5563',
            lineHeight: 1.45,
            marginBottom: 6,
          }}
        >
          {copy.intro.split('**').map((chunk, i) =>
            i % 2 === 1 ? (
              <strong key={i}>{chunk}</strong>
            ) : (
              <span key={i}>{chunk}</span>
            )
          )}
        </div>
        <div
          style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: '14px',
            color: '#374151',
            lineHeight: 1.4,
          }}
        >
          {copy.hint}
        </div>
      </div>

      {natureNote && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 12,
            background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
            border: '1px solid #86EFAC',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontWeight: 800,
              fontSize: '14px',
              color: '#14532D',
              marginBottom: 6,
            }}
          >
            🌿 {copy.natureHeading} · {natureNote.letter} — {natureNote.animal}
          </div>
          <div
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '16px',
              color: '#166534',
              lineHeight: 1.5,
            }}
          >
            {natureNote.text}
          </div>
        </div>
      )}

      <div
        style={{
          overflowY: 'auto',
          flex: 1,
          minHeight: 0,
          paddingRight: 4,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
            gap: 6,
          }}
        >
          {animalAlphabetData.map((item) => {
            const animal = isEn ? item.animalEn : item.animalEs;
            const emoji = isEn ? item.emojiEn : item.emojiEs;
            const funFact = isEn ? item.funFactEn : item.funFactEs;
            return (
              <button
                key={item.letter}
                type="button"
                onClick={() => {
                  playClickFeedback();
                  speakLetterAnimalAndNature(item.letter, animal, funFact, lang);
                  setNatureNote({ letter: item.letter, animal, text: funFact });
                  onPickAnimal(animal);
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  padding: '8px 4px',
                  borderRadius: 10,
                  border: '2px solid #BBF7D0',
                  background: '#fff',
                  cursor: 'pointer',
                  fontFamily: "'Nunito', sans-serif",
                  transition: 'transform 0.12s ease, box-shadow 0.12s ease',
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.97)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title={`${item.letter} — ${animal}`}
              >
                <span style={{ fontSize: '18px', fontWeight: 800, color: '#15803D' }}>{item.letter}</span>
                <span style={{ fontSize: '20px', lineHeight: 1 }} aria-hidden>
                  {emoji}
                </span>
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#374151',
                    textAlign: 'center',
                    lineHeight: 1.15,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {animal}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AnimalAlphabetChatEmbed;
